import { PROTOCOL_CONSTANTS } from "@klass-share/protocol";
import { prepareStrokePoints } from "@klass-share/whiteboard-core";
import type {
  WhiteboardAwarenessState,
  WhiteboardCursor,
  WhiteboardPresentationState,
  WhiteboardPreview,
} from "@klass-share/whiteboard-core";
import type { Awareness } from "y-protocols/awareness";

export interface AwarenessClock {
  now(): number;
  setTimeout(
    callback: () => void,
    delay: number,
  ): ReturnType<typeof setTimeout>;
  clearTimeout(handle: ReturnType<typeof setTimeout>): void;
}

interface LocalAwarenessUpdate {
  readonly name?: string;
  readonly colorSeed?: number;
  readonly cursor?: WhiteboardCursor | undefined;
  readonly preview?: WhiteboardPreview | undefined;
}

const defaultClock: AwarenessClock = {
  now: () => performance.now(),
  setTimeout: (callback, delay) => setTimeout(callback, delay),
  clearTimeout: (handle) => clearTimeout(handle),
};

export class WhiteboardAwarenessController {
  readonly #awareness: Awareness;
  readonly #clock: AwarenessClock;
  #pending: LocalAwarenessUpdate | undefined;
  #timer: ReturnType<typeof setTimeout> | undefined;
  #lastPublishedAt = Number.NEGATIVE_INFINITY;

  public constructor(
    awareness: Awareness,
    identity: Pick<WhiteboardAwarenessState, "name" | "colorSeed">,
    clock: AwarenessClock = defaultClock,
  ) {
    this.#awareness = awareness;
    this.#clock = clock;
    this.#publish(identity);
  }

  public updateCursor(cursor: WhiteboardCursor | undefined): void {
    this.#queue({ cursor });
  }

  public updatePreview(preview: WhiteboardPreview | undefined): void {
    const safePreview =
      preview === undefined
        ? undefined
        : {
            ...preview,
            "pts-tail": prepareStrokePoints(preview["pts-tail"]).slice(-64),
          };
    this.#queue({ preview: safePreview });
  }

  public clearTransient(): void {
    this.#pending = undefined;
    if (this.#timer !== undefined) this.#clock.clearTimeout(this.#timer);
    this.#timer = undefined;
    this.#publish({ cursor: undefined, preview: undefined });
  }

  public destroy(): void {
    if (this.#timer !== undefined) this.#clock.clearTimeout(this.#timer);
    this.#timer = undefined;
    this.#pending = undefined;
  }

  #queue(update: LocalAwarenessUpdate): void {
    this.#pending = { ...this.#pending, ...update };
    const elapsed = this.#clock.now() - this.#lastPublishedAt;
    if (elapsed >= PROTOCOL_CONSTANTS.WB_AWARENESS_MIN_INTERVAL) {
      this.#flush();
      return;
    }
    if (this.#timer !== undefined) return;
    this.#timer = this.#clock.setTimeout(
      () => this.#flush(),
      PROTOCOL_CONSTANTS.WB_AWARENESS_MIN_INTERVAL - elapsed,
    );
  }

  #flush(): void {
    if (this.#timer !== undefined) this.#clock.clearTimeout(this.#timer);
    this.#timer = undefined;
    const pending = this.#pending;
    this.#pending = undefined;
    if (pending !== undefined) this.#publish(pending);
  }

  #publish(update: LocalAwarenessUpdate): void {
    const current = asAwarenessState(this.#awareness.getLocalState());
    const next = compactLocalState({
      name: update.name ?? current?.name ?? "",
      colorSeed: update.colorSeed ?? current?.colorSeed ?? 0,
      cursor: "cursor" in update ? update.cursor : current?.cursor,
      preview: "preview" in update ? update.preview : current?.preview,
    });
    const bounded = fitAwarenessState(next);
    this.#awareness.setLocalState(
      bounded as unknown as Record<string, unknown>,
    );
    this.#lastPublishedAt = this.#clock.now();
  }
}

export function hostPresentationState(
  awareness: Pick<Awareness, "getStates">,
  fallbackPage = "default",
): WhiteboardPresentationState {
  const hosts: Array<readonly [number, WhiteboardAwarenessState]> = [];
  for (const [clientId, value] of awareness.getStates()) {
    const state = asAwarenessState(value);
    if (state?.role === "host") hosts.push([clientId, state]);
  }
  hosts.sort((left, right) => left[0] - right[0]);
  const host = hosts[0]?.[1];
  return {
    activePage:
      typeof host?.activePage === "string" && host.activePage.length > 0
        ? host.activePage
        : fallbackPage,
    boardLocked: host?.boardLocked === true,
    ...(host?.hostTime === undefined ? {} : { hostTime: host.hostTime }),
  };
}

export function remoteAwarenessStates(
  awareness: Pick<Awareness, "clientID" | "getStates">,
): ReadonlyArray<readonly [number, WhiteboardAwarenessState]> {
  const states: Array<readonly [number, WhiteboardAwarenessState]> = [];
  for (const [clientId, value] of awareness.getStates()) {
    if (clientId === awareness.clientID) continue;
    const state = asAwarenessState(value);
    if (state !== undefined) states.push([clientId, state]);
  }
  return states;
}

function fitAwarenessState(
  state: WhiteboardAwarenessState,
): WhiteboardAwarenessState {
  let candidate = state;
  while (
    awarenessByteLength(candidate) >
      PROTOCOL_CONSTANTS.WB_AWARENESS_MAX_BYTES &&
    candidate.preview !== undefined &&
    candidate.preview["pts-tail"].length > 4
  ) {
    candidate = {
      ...candidate,
      preview: {
        ...candidate.preview,
        "pts-tail": candidate.preview["pts-tail"].slice(2),
      },
    };
  }
  if (
    awarenessByteLength(candidate) <= PROTOCOL_CONSTANTS.WB_AWARENESS_MAX_BYTES
  )
    return candidate;
  const withoutPreview = compactLocalState({
    name: candidate.name,
    colorSeed: candidate.colorSeed,
    cursor: candidate.cursor,
    preview: undefined,
  });
  if (
    awarenessByteLength(withoutPreview) <=
    PROTOCOL_CONSTANTS.WB_AWARENESS_MAX_BYTES
  )
    return withoutPreview;
  return {
    name: [...state.name].slice(0, 64).join(""),
    colorSeed: state.colorSeed,
  };
}

function awarenessByteLength(value: WhiteboardAwarenessState): number {
  return new TextEncoder().encode(JSON.stringify(value)).byteLength;
}

function asAwarenessState(
  value: unknown,
): WhiteboardAwarenessState | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const state = value as Partial<WhiteboardAwarenessState>;
  const hostState = state.role === "host";
  const name =
    typeof state.name === "string"
      ? state.name
      : hostState
        ? "host"
        : undefined;
  const colorSeed =
    typeof state.colorSeed === "number" && Number.isFinite(state.colorSeed)
      ? state.colorSeed
      : hostState
        ? 0
        : undefined;
  const hostTime =
    hostState &&
    typeof state.hostTime === "number" &&
    Number.isFinite(state.hostTime) &&
    state.hostTime >= 0
      ? state.hostTime
      : undefined;
  if (
    name === undefined ||
    colorSeed === undefined ||
    (state.cursor !== undefined && !isCursor(state.cursor)) ||
    (state.preview !== undefined && !isPreview(state.preview)) ||
    (state.role !== undefined && state.role !== "host") ||
    (state.activePage !== undefined &&
      (typeof state.activePage !== "string" ||
        state.activePage.length === 0)) ||
    (state.boardLocked !== undefined && typeof state.boardLocked !== "boolean")
  )
    return undefined;
  const parsed: WhiteboardAwarenessState = {
    name,
    colorSeed,
    ...(state.cursor === undefined ? {} : { cursor: state.cursor }),
    ...(state.preview === undefined ? {} : { preview: state.preview }),
    ...(state.role === undefined ? {} : { role: state.role }),
    ...(state.activePage === undefined ? {} : { activePage: state.activePage }),
    ...(state.boardLocked === undefined
      ? {}
      : { boardLocked: state.boardLocked }),
    ...(hostTime === undefined ? {} : { hostTime }),
  };
  return awarenessByteLength(parsed) <=
    PROTOCOL_CONSTANTS.WB_AWARENESS_MAX_BYTES
    ? parsed
    : undefined;
}

function compactLocalState(state: {
  readonly name: string;
  readonly colorSeed: number;
  readonly cursor: WhiteboardCursor | undefined;
  readonly preview: WhiteboardPreview | undefined;
}): WhiteboardAwarenessState {
  return {
    name: state.name,
    colorSeed: state.colorSeed,
    ...(state.cursor === undefined ? {} : { cursor: state.cursor }),
    ...(state.preview === undefined ? {} : { preview: state.preview }),
  };
}

function isCursor(value: unknown): value is WhiteboardCursor {
  if (typeof value !== "object" || value === null) return false;
  const cursor = value as Partial<WhiteboardCursor>;
  return (
    typeof cursor.page === "string" &&
    cursor.page.length > 0 &&
    typeof cursor.x === "number" &&
    Number.isFinite(cursor.x) &&
    typeof cursor.y === "number" &&
    Number.isFinite(cursor.y)
  );
}

function isPreview(value: unknown): value is WhiteboardPreview {
  if (typeof value !== "object" || value === null) return false;
  const preview = value as Partial<WhiteboardPreview>;
  return (
    (preview.tool === "pen" ||
      preview.tool === "highlighter" ||
      preview.tool === "eraser-stroke") &&
    typeof preview.color === "string" &&
    /^#[0-9a-f]{6}$/iu.test(preview.color) &&
    typeof preview.width === "number" &&
    Number.isFinite(preview.width) &&
    preview.width > 0 &&
    Array.isArray(preview["pts-tail"]) &&
    preview["pts-tail"].length % 2 === 0 &&
    preview["pts-tail"].every(
      (coordinate) =>
        typeof coordinate === "number" && Number.isFinite(coordinate),
    )
  );
}
